import { useEffect, useRef, useState } from "react";

const PRIMARY_TABS = [
  { key: "overview",    icon: "📋", label: "Übersicht" },
  { key: "program",     icon: "🗓", label: "Programm" },
  { key: "map",         icon: "🗺", label: "Karte" },
  { key: "accounting",  icon: "💰", label: "Ausgaben" },
] as const;

const MORE_TABS = [
  { key: "participants", icon: "👥", label: "Gruppe & Teilnehmer" },
  { key: "memories",     icon: "📖", label: "Erinnerungen" },
  { key: "tasks",        icon: "✅", label: "Aufgaben" },
  { key: "packlist",     icon: "🎒", label: "Packliste" },
  { key: "documents",    icon: "📁", label: "Dokumente" },
  { key: "suggestions",  icon: "💡", label: "Vorschläge & Ideen" },
] as const;

type PrimaryTabKey = (typeof PRIMARY_TABS)[number]["key"];
type MoreTabKey = (typeof MORE_TABS)[number]["key"];
export type TabKey = PrimaryTabKey | MoreTabKey;

interface TripTabsProps {
  activeTab: TabKey;
  setActiveTab: (tab: TabKey) => void;
  isOrganizer: boolean;
  children: {
    overview: React.ReactNode;
    program: React.ReactNode;
    map?: React.ReactNode;
    accounting: React.ReactNode;
    participants: React.ReactNode;
    memories?: React.ReactNode;
    tasks?: React.ReactNode;
    packlist?: React.ReactNode;
    documents?: React.ReactNode;
    suggestions?: React.ReactNode;
  };
}

const isMoreTab = (key: TabKey): key is MoreTabKey =>
  MORE_TABS.some((t) => t.key === key);

export default function TripTabs({
  activeTab,
  setActiveTab,
  isOrganizer,
  children,
}: TripTabsProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  // Schliessen bei Klick ausserhalb
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        moreButtonRef.current &&
        !moreButtonRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleMoreTabSelect = (key: MoreTabKey) => {
    setActiveTab(key);
    setDropdownOpen(false);
  };

  const moreIsActive = isMoreTab(activeTab);

  return (
    <div className="w-full">
      {/* Tab-Leiste – scrollt mit der Seite */}
      <div className="bg-[#161b27] border-b border-[#2a3347]">
        <div className="flex items-stretch">
          {/* 4 Primär-Tabs */}
          {PRIMARY_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-3 px-3 sm:px-4 text-sm font-medium border-b-2 whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                activeTab === tab.key
                  ? "border-sky-400 text-sky-400"
                  : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
            >
              {tab.icon}
              <span className="text-[11px] sm:text-sm ml-1">{tab.label}</span>
            </button>
          ))}

          {/* ··· Mehr-Button */}
          <div className="relative ml-auto flex-shrink-0">
            <button
              ref={moreButtonRef}
              onClick={() => setDropdownOpen((prev) => !prev)}
              className={`h-full py-3 px-4 text-sm font-medium border-b-2 whitespace-nowrap transition-all duration-200 ${
                moreIsActive || dropdownOpen
                  ? "border-sky-400 text-sky-400"
                  : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
              aria-label="Mehr Tabs"
            >
              {moreIsActive
                ? MORE_TABS.find((t) => t.key === activeTab)?.icon ?? "···"
                : "···"}
              <span className="text-[11px] sm:text-sm ml-1">
                {moreIsActive
                  ? MORE_TABS.find((t) => t.key === activeTab)?.label
                  : "Mehr"}
              </span>
            </button>

            {/* Dropdown Popover */}
            {dropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute top-full right-0 mt-1 w-56 bg-[#161b27] border border-[#2a3347] rounded-xl shadow-xl z-50 overflow-hidden"
              >
                {MORE_TABS.filter((tab) => tab.key !== "tasks" || isOrganizer).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleMoreTabSelect(tab.key)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                      activeTab === tab.key
                        ? "bg-[#1e2639] text-sky-400"
                        : "text-slate-300 hover:bg-[#1e2639]"
                    }`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab-Inhalte */}
      <div className="pb-24">
        {activeTab === "overview" && children.overview}
        {activeTab === "program" && children.program}
        {activeTab === "map" && (
          children.map ?? (
            <div className="text-center py-16">
              <p className="text-4xl mb-3">🗺</p>
              <p className="text-slate-400 text-sm">Karten-Ansicht folgt bald.</p>
            </div>
          )
        )}
        {activeTab === "accounting" && children.accounting}
        {activeTab === "participants" && children.participants}
        {activeTab === "memories" && (
          children.memories ?? (
            <div className="text-center py-16">
              <p className="text-4xl mb-3">📖</p>
              <p className="text-slate-400 text-sm">Erinnerungen folgen bald.</p>
            </div>
          )
        )}
        {activeTab === "tasks" && children.tasks}
        {activeTab === "packlist" && children.packlist}
        {activeTab === "documents" && children.documents}
        {activeTab === "suggestions" && (
          children.suggestions ?? (
            <div className="text-center py-16">
              <p className="text-4xl mb-3">💡</p>
              <p className="text-slate-400 text-sm">Vorschläge & Ideen folgen bald.</p>
            </div>
          )
        )}
      </div>
    </div>
  );
}
